import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './api/auth.service';
import { NotificationService } from './notification.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private timeoutId: ReturnType<typeof setTimeout> | undefined;
  private warningTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private countdownIntervalId: ReturnType<typeof setInterval> | undefined;
  private readonly inactivityTimeout = 5 * 60 * 1000;
  private readonly warningDuration = 60 * 1000;

  private readonly events = [
    'mousemove',
    'mousedown',
    'keypress',
    'keydown',
    'keyup',
    'touchstart',
    'scroll',
    'wheel',
    'pointermove',
    'pointerdown',
    'click',
    'contextmenu'
  ];

  private showWarningSubject = new BehaviorSubject<boolean>(false);
  public showWarning$ = this.showWarningSubject.asObservable();

  private listenersActive = true;
  private listenersAdded = false;

  private countdownSubject = new BehaviorSubject<number>(60);
  public countdown$ = this.countdownSubject.asObservable();

  private resetTimerHandler = () => this.resetTimer();


  constructor(
    private ngZone: NgZone,
    private _authService: AuthService,
    private _translate: TranslateService,
    private _notificationService: NotificationService
  ) { }

  init() {
    this.resetTimer();
    this.addActivityListeners();
  }
 
  destroy() {
    if (this.timeoutId != null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    if (this.warningTimeoutId != null) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = undefined;
    }
    if (this.countdownIntervalId != null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = undefined;
    }
    this.removeActivityListeners();
    this.showWarningSubject.next(false);
    this.countdownSubject.next(60);
  }

  private enableActivityListeners() {
    if (!this.listenersActive) {
      this.listenersActive = true;
      this.addActivityListeners();
    }
  }

  private disableActivityListeners() {
    if (this.listenersActive) {
      this.listenersActive = false;
      this.removeActivityListeners();
    }
  }

  resumeSession() {
    this.showWarningSubject.next(false);
    if (this.warningTimeoutId != null) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = undefined;
    }
    if (this.countdownIntervalId != null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = undefined;
    }
    this.enableActivityListeners();
    this.resetTimer();
  }

  forceLogout() {
    this.handleLogout();
  }


  private addActivityListeners() {
    if (this.listenersActive && !this.listenersAdded) {
      this.listenersAdded = true;
      // Use non-capturing listeners to avoid interfering with normal event handling
      this.events.forEach(event =>
        window.addEventListener(event, this.resetTimerHandler, { passive: true })
      );
      // Add visibility change listener to detect tab switching
      document.addEventListener('visibilitychange', this.resetTimerHandler, { passive: true });
    }
  }

  private removeActivityListeners() {
    if (this.listenersAdded) {
      this.listenersAdded = false;
      this.events.forEach(event =>
        window.removeEventListener(event, this.resetTimerHandler)
      );
      // Remove visibility change listener
      document.removeEventListener('visibilitychange', this.resetTimerHandler);
    }
  }

  private resetTimer() {
    if (this.timeoutId != null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    if (this.warningTimeoutId != null) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = undefined;
    }
    this.showWarningSubject.next(false);

    // Run timeout outside Angular zone to prevent unnecessary change detection
    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = setTimeout(() => {
        this.ngZone.run(() => this.showWarning());
      }, this.inactivityTimeout);
    });
  }

  private showWarning() {
    // Clear main inactivity timeout so it doesn't fire again
    if (this.timeoutId != null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    this.showWarningSubject.next(true);
    this.countdownSubject.next(60);

    this.disableActivityListeners();

    // Run warning timeout outside Angular zone
    this.ngZone.runOutsideAngular(() => {
      this.warningTimeoutId = setTimeout(() => {
        this.ngZone.run(() => this.handleLogout());
      }, this.warningDuration);
    });

    this.startCountdown();
  }

  private startCountdown() {
    if (this.countdownIntervalId != null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = undefined;
    }

    this.countdownIntervalId = setInterval(() => {
      const current = this.countdownSubject.value;
      if (current <= 1) {
        if (this.countdownIntervalId != null) {
          clearInterval(this.countdownIntervalId);
          this.countdownIntervalId = undefined;
        }
        this.countdownSubject.next(0);
      } else {
        this.countdownSubject.next(current - 1);
      }
    }, 1000);
  }

  private handleLogout() {
    this.showWarningSubject.next(false);
    // Clear all timers so nothing fires after logout
    if (this.timeoutId != null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
    if (this.warningTimeoutId != null) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = undefined;
    }
    if (this.countdownIntervalId != null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = undefined;
    }
    this.removeActivityListeners();
    this.listenersActive = true;
    this.listenersAdded = false;
    this.performLogout();
  }

  private performLogout() {
    localStorage.removeItem('auth');
    this._authService.logout();

    this._notificationService.warning(
      this._translate.instant('sessionExpired'),
      this._translate.instant('yourSessionHasExpiredDueToInactivity'),
      {
        position: 'top-right',
        showCloseButton: true,
        duration: 0,
        color: 'warning',
      }
    );
    // Clean up service state after logout (e.g. if app doesn't full reload)
    this.destroy();
  }
}
